import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { CheckCircleOutlined, CancelOutlined, CloseOutlined, OpenInNewOutlined } from '@mui/icons-material';

function statusStyles(status) {
  const str =
    typeof status === 'object' && status !== null ? status.Status ?? status.ApproverUIStatus ?? '' : status ?? '';
  const map = {
    Pending: { bg: 'bg-[#FEF3C7]', text: 'text-[#D97706]', dot: 'bg-[#F59E0B]' },
    Submitted: { bg: 'bg-[#E6F4F8]', text: 'text-[#0094B3]', dot: 'bg-[#0094B3]' },
    Approved: { bg: 'bg-[#D1FAE5]', text: 'text-[#059669]', dot: 'bg-[#10B981]' },
    Rejected: { bg: 'bg-[#FEE2E2]', text: 'text-[#DC2626]', dot: 'bg-[#EF4444]' },
    Draft: { bg: 'bg-[#F1F5F9]', text: 'text-[#64748B]', dot: 'bg-[#94A3B8]' }
  };
  return { label: str, ...(map[str] ?? map.Draft) };
}

function timesheetPreview(item) {
  const days =
    item.TimesheetDetails?.[0]?.TimesheetHoursByDates ??
    item.TimesheetSections?.[0]?.TimesheetDetails?.[0]?.TimesheetHoursByDates;
  let period = item.PeriodLabel ?? '—';
  if (days?.length) {
    const start = dayjs(days[0].Day);
    const end = dayjs(days.at(-1).Day);
    period = start.isSame(end, 'month')
      ? `${start.format('D')} – ${end.format('D MMM YYYY')}`
      : `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
  }
  const contractorName = `${item.ContractorFirstName ?? ''} ${item.ContractorLastName ?? ''}`.trim();
  return {
    title: period,
    contractorName: contractorName || item.ClientName || '—',
    rows: [
      { label: 'Client', value: item.ClientName ?? '—' },
      { label: 'Reference', value: item.TSNumber ?? item.Reference ?? '—' }
    ],
    detailPath: `/dashboard/timesheet/${item.Id}`
  };
}

function expensePreview(item) {
  const contractorName =
    item.User?.FirstName || item.User?.LastName
      ? `${item.User.FirstName ?? ''} ${item.User.LastName ?? ''}`.trim()
      : item.ClientName ?? '—';
  const total = item.TotalExpense ?? item.TotalAmount ?? 0;
  const symbol = item.ExpenseCurrency?.Symbol ?? item.ExpenseDetails?.[0]?.ExpenseCurrency?.Symbol ?? '£';
  const lineCount = item.ExpenseDetails?.length ?? item.LineItemCount ?? 0;
  return {
    title: item.ClaimDescription ?? item.ClaimName ?? '—',
    contractorName,
    rows: [
      {
        label: 'Amount',
        value: `${symbol}${parseFloat(total).toLocaleString('en-GB', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`
      },
      { label: 'Line items', value: lineCount || '—' }
    ],
    detailPath: `/dashboard/expense/${item.Id}`
  };
}

const DISMISS_THRESHOLD = 90;

export default function QuickLookDrawer({ open, onClose, item, type = 'timesheet', onApprove, onReject }) {
  const navigate = useNavigate();
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(null); // 'approve' | 'reject' | null
  const startY = useRef(0);

  useEffect(() => {
    if (open) {
      setDragY(0);
      setBusy(null);
    }
  }, [open, item]);

  if (!open || !item) return null;

  const preview = type === 'expense' ? expensePreview(item) : timesheetPreview(item);
  const st = statusStyles(item.Status);
  const canReview = !!(onApprove || onReject);

  const handlePointerDown = (e) => {
    startY.current = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    setDragging(true);
  };
  const handlePointerMove = (e) => {
    if (!dragging) return;
    const y = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    setDragY(Math.max(0, y - startY.current));
  };
  const handlePointerUp = () => {
    setDragging(false);
    if (dragY > DISMISS_THRESHOLD) onClose();
    else setDragY(0);
  };

  const runAction = async (kind) => {
    setBusy(kind);
    try {
      if (kind === 'approve') await onApprove?.(item.Id);
      else await onReject?.(item.Id);
      onClose();
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <style>{`
        @keyframes ql-backdrop-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes ql-slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .ql-backdrop-in { animation: ql-backdrop-in 0.18s ease both; }
        .ql-slide-up { animation: ql-slide-up 0.25s cubic-bezier(0.32,0.72,0,1) both; }
      `}</style>

      <div role="presentation" className="ql-backdrop-in fixed inset-0 z-[200] bg-black/40" onClick={onClose} />

      <div
        className="ql-slide-up fixed left-0 right-0 bottom-0 z-[210] bg-white rounded-t-[18px] overflow-hidden"
        style={{
          transform: `translateY(${dragY}px)`,
          transition: dragging ? 'none' : 'transform 0.2s cubic-bezier(0.4,0,0.2,1)',
          paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)'
        }}
      >
        {/* Drag handle — swipe down to dismiss */}
        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={() => dragging && handlePointerUp()}
          className="flex items-center justify-center py-3 cursor-grab active:cursor-grabbing touch-none"
        >
          <span className="w-10 h-1 rounded-full bg-[#E2E8ED]" />
        </div>

        <div className="flex items-start justify-between px-5 pb-3">
          <div className="min-w-0">
            <h3 className="text-[17px] font-bold text-[#022D41] m-0 leading-tight truncate">{preview.title}</h3>
            <p className="text-[13px] text-[#6B7A85] mt-0.5 m-0 truncate">{preview.contractorName}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            {st.label && (
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold ${st.bg} ${st.text}`}
              >
                <span className={`w-[5px] h-[5px] rounded-full shrink-0 ${st.dot}`} />
                {st.label}
              </span>
            )}
            <button onClick={onClose} className="bg-transparent border-none cursor-pointer text-[#6B7A85] p-1">
              <CloseOutlined sx={{ fontSize: 18 }} />
            </button>
          </div>
        </div>

        <div className="px-5 pb-4">
          <div className="bg-[#F8FAFB] rounded-[10px] border border-[#E2E8ED] divide-y divide-[#E2E8ED]">
            {preview.rows.map((r) => (
              <div key={r.label} className="flex items-center justify-between px-4 py-2.5">
                <span className="text-[13px] text-[#6B7A85]">{r.label}</span>
                <span className="text-[13.5px] font-semibold text-[#022D41]">{r.value}</span>
              </div>
            ))}
          </div>

          <button
            onClick={() => {
              onClose();
              navigate(preview.detailPath);
            }}
            className="flex items-center justify-center gap-1.5 w-full mt-3 py-2.5 text-[13.5px] font-semibold text-[#0094B3] bg-transparent border border-[#E2E8ED] rounded-[8px] cursor-pointer hover:bg-[#F8FAFB] transition-colors"
          >
            View full details <OpenInNewOutlined sx={{ fontSize: 14 }} />
          </button>
        </div>

        {canReview && (
          <div className="flex gap-3 px-5">
            <button
              onClick={() => runAction('reject')}
              disabled={!!busy}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-[10px] border border-[#FECACA] text-[#DC2626] text-[15px] font-semibold bg-white cursor-pointer active:scale-[0.98] transition-transform disabled:opacity-50"
            >
              <CancelOutlined sx={{ fontSize: 18 }} />
              {busy === 'reject' ? 'Rejecting…' : 'Reject'}
            </button>
            <button
              onClick={() => runAction('approve')}
              disabled={!!busy}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-[10px] bg-[#10B981] text-white text-[15px] font-semibold border-none cursor-pointer active:scale-[0.98] transition-transform disabled:opacity-50"
            >
              <CheckCircleOutlined sx={{ fontSize: 18 }} />
              {busy === 'approve' ? 'Approving…' : 'Approve'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
